import React, { useEffect, useRef, useState } from 'react';
import { motion as Motion, animate, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Play, Image as ImageIcon, Zap, Accessibility, Search, Bot, MousePointerClick, RefreshCw, Laptop, Chrome, Globe, Database, Users, Target } from 'lucide-react';
import SectionHeading from '../campaigns/components/SectionHeading';
import TeaserVideo from '../campaigns/components/TeaserVideo';
import {
  HeroMarqueeTrack,
  CropMarks,
  IndexTag,
  LiveCoordTag,
  RegistrationMark,
  VerticalLabel,
  SectionFrame,
  SectionCounter,
  GrainOverlay,
  GridOverlay,
} from '../../components/swiss/SwissKit';
import { Nav, TRANSLATIONS, BookingModal, trackEvent, SiteFooter } from '../LandingPage';
import { PrimaryButton, PrimaryLink, SolveRow } from '../seo/SolutionPageTemplate';
import { StatsStrip } from '../../components/Stats';

/* --------------------------------------------------------------------------------
   CASE STUDY — MINIMAL BAR BERLIN · /de|en/case-studies/minimal-bar

   Problem → system → before/after → result, in the same Swiss-poster
   language as the solution pages (magenta poster hero, section frames,
   SectionHeading, SolveRow, shared Nav/footer/booking wizard). Filed under
   the AI tab: the story is process automation, and "Automation" is the
   booking wizard's matching AI reason.
-------------------------------------------------------------------------------- */

const MAGENTA = 'var(--cre8-magenta)';
const YELLOW = 'var(--cre8-yellow)';
const ACCENT = 'var(--ai-primary)';
const ORIGIN = 'https://cre8now.design';
const SITE_URL = 'https://minimal.berlin';
const TOTAL_SECTIONS = 6;

/* Final images and videos go into public/assets/case-studies/minimal-bar/
   and their paths here. Every slot left at null renders a labeled
   placeholder in the right aspect ratio, so the layout is final already. */
const MEDIA_BASE = '/assets/case-studies/minimal-bar';
// Bump on every re-render of the film so browsers and CDNs drop the cached file.
const FILM_VERSION = 7;
const MEDIA = {
  // 46 s seamless-loop case-study film (1920×900, 60 fps) with soundtrack, rendered from motion/minimal-bar/.
  walkthrough: [
    { src: `${MEDIA_BASE}/case-film-hevc.mp4?v=${FILM_VERSION}`, type: 'video/mp4; codecs="hvc1.1.6.L123.90"' },
    { src: `${MEDIA_BASE}/case-film.mp4?v=${FILM_VERSION}`, type: 'video/mp4; codecs="avc1.64002a"' },
  ],
  walkthroughPoster: `${MEDIA_BASE}/case-film-poster.webp?v=${FILM_VERSION}`,
  // Screen recording of the booking app (mini-app.mov), 1920×1080.
  app: [
    { src: `${MEDIA_BASE}/booking-app-walkthrough-hevc.mp4`, type: 'video/mp4; codecs="hvc1.1.6.L93.B0"' },
    { src: `${MEDIA_BASE}/booking-app-walkthrough.mp4`, type: 'video/mp4; codecs="avc1.640028"' },
  ],
  appPoster: `${MEDIA_BASE}/booking-app-walkthrough-poster.webp`,
  // Chrome companion side panel (mini-app-companion.mov from 02:12), cropped
  // in on the extension: sync dialogs per platform, then a form filling itself.
  companion: [
    { src: `${MEDIA_BASE}/companion-walkthrough-hevc.mp4`, type: 'video/mp4; codecs="hvc1.1.6.L93.B0"' },
    { src: `${MEDIA_BASE}/companion-walkthrough.mp4`, type: 'video/mp4; codecs="avc1.640028"' },
  ],
  companionPoster: `${MEDIA_BASE}/companion-walkthrough-poster.webp`,
  // Screen recording of minimal.berlin (scroll + clicks), H.264 1280×800.
  website: [
    { src: `${MEDIA_BASE}/website-walkthrough-hevc.mp4`, type: 'video/mp4; codecs="hvc1.1.6.L93.B0"' },
    { src: `${MEDIA_BASE}/website-walkthrough.mp4`, type: 'video/mp4; codecs="avc1.640028"' },
  ],
  websitePoster: `${MEDIA_BASE}/website-walkthrough-poster.webp`,
  // Screenshot rows under the three components, one group per component.
  // Convert new shots with scripts/optimize-images.sh first; src: null
  // renders a placeholder until the file exists.
  screenGroups: [
    {
      de: 'Booking-App', en: 'Booking app',
      items: [
        { src: `${MEDIA_BASE}/booking-app-kalender.webp`, de: 'Kalender: der ganze Monat auf einen Blick', en: 'Calendar: the whole month at a glance' },
        { src: `${MEDIA_BASE}/booking-app-liste.webp`, de: 'Liste: das Programm Tag für Tag', en: 'List: the programme day by day' },
        { src: `${MEDIA_BASE}/booking-app-reservierungen.webp`, de: 'Reservierungen aus der Website, direkt im Tool', en: 'Reservations from the website, right in the tool' },
        { src: `${MEDIA_BASE}/booking-app-event-bearbeiten.webp`, de: 'Event anlegen: einmal eintragen, überall live', en: 'Creating an event: enter once, live everywhere' },
      ],
    },
    {
      de: 'Chrome-Extension', en: 'Chrome extension',
      items: [
        { src: `${MEDIA_BASE}/companion-sync.webp`, de: 'Sync: Der Companion findet fehlende Termine auf jeder Plattform', en: 'Sync: the companion finds missing dates on every platform' },
        { src: `${MEDIA_BASE}/companion-formular.webp`, de: 'Jedes Formular füllt der Companion Schritt für Schritt selbst aus', en: 'The companion fills in every form step by step' },
        { src: `${MEDIA_BASE}/companion-veroeffentlicht.webp`, de: 'Veröffentlicht: jedes Event geht live auf der Plattform', en: 'Published: every event goes live on the platform' },
      ],
    },
    {
      de: 'Website', en: 'Website',
      items: [
        { src: `${MEDIA_BASE}/website-start.webp`, de: 'Startseite: das Event des Tages, täglich automatisch aktuell', en: 'Homepage: tonight\'s event, updated automatically every day' },
        { src: `${MEDIA_BASE}/website-events.webp`, de: 'Event-Übersicht: alle Events automatisch aus der App gesynct', en: 'Event overview: every event synced automatically from the app' },
        { src: `${MEDIA_BASE}/website-event.webp`, de: 'Event-Detailseite, direkt aus der App gespeist', en: 'Event page, fed straight from the app' },
        { src: `${MEDIA_BASE}/website-djs.webp`, de: 'DJs: die größte Sammlung, jeden Tag kommen neue dazu', en: 'DJs: the biggest collection, with new DJs added every day' },
        { src: `${MEDIA_BASE}/website-dj-player.webp`, de: 'DJ-Seite mit integriertem Player', en: 'DJ page with built-in player' },
        { src: `${MEDIA_BASE}/website-reservierung.webp`, de: 'Reservierung: Anfragen landen im Tool', en: 'Reservations: requests land in the tool' },
        { src: `${MEDIA_BASE}/website-faq.webp`, de: 'FAQ: alle offenen Fragen vorab geklärt', en: 'FAQ: every open question answered upfront' },
        { src: `${MEDIA_BASE}/website-galerie.webp`, de: 'Galerie: die Räume der Bar', en: 'Gallery: the rooms of the bar' },
        { src: `${MEDIA_BASE}/website-anfahrt.webp`, de: 'Anfahrt, Öffnungszeiten und Karte', en: 'Directions, opening hours and map' },
      ],
    },
  ],
};

/* Google Lighthouse / PageSpeed scores for minimal.berlin (mobile run).
   Keep these the real measured values — the page shows them as-is, colored
   with Lighthouse's own thresholds. Re-run and update after site changes. */
const LIGHTHOUSE = { performance: 84, accessibility: 100, bestPractices: 100, seo: 100 };
const AGENTIC_BROWSING = { passed: 3, total: 3 };

const CONTENT = {
  de: {
    htmlLang: 'de',
    path: '/de/case-studies/minimal-bar',
    title: 'CRE8NOW | Case Study Minimal Bar Berlin: 50 Std. → 30 Min.',
    description: 'Booking Mac-App, Chrome-Companion und Website in einem System: Aus ~50 Std. Handarbeit im Monat wurden bei der Minimal Bar Berlin 30 Min. 0 € laufende Kosten.',
    ogImage: '/assets/og/cre8now_case-minimal-bar-de.jpg',
    breadcrumb: ['CRE8NOW', 'CASE STUDIES', 'MINIMAL BAR'],
    hero: {
      headline: ['Von 40–50 Stunden Handarbeit auf', '30 MINUTEN'],
      copy: 'Die Minimal Bar in Berlin-Friedrichshain hat jeden Monat 40 bis 50 Stunden in dieselben Handgriffe gesteckt: Bookings in den Kalender, jedes Event einzeln auf jede Plattform, Flyer, Social Posts, Reservierungen. CRE8NOW hat daraus ein System gebaut, aus einer Mac-App, einem Chrome-Companion und der ersten eigenen Website nach 16 Jahren. Heute reicht eine einzige Eingabe.',
      cta: 'minimal.berlin ansehen',
      marquee: ['Booking-App für macOS', 'Chrome-Companion', 'Event-Sync', 'Social Sharing', 'Reservierungen', 'Website minimal.berlin', 'Ohne KI', '0 € Betriebskosten', 'LLM-ready', '40–50 h → 30 Min'],
    },
    stats: [
      { n: '~50 Std', l: 'Handarbeit pro Monat vorher' },
      { n: '30 Min', l: 'pro Monat heute' },
      { n: '0 €', l: 'laufende Kosten' },
    ],
    problem: {
      label: 'CASE STUDY / AUSGANGSLAGE',
      title: 'Jeden Monat',
      highlight: 'VON VORN',
      description: 'Bevor CRE8NOW eingestiegen ist, lief das komplette Programm der Minimal Bar über Handarbeit. Jedes Booking, jedes Event und jeder Post wurde einzeln angelegt, Monat für Monat, auf jeder Plattform neu. Die Arbeit hing an Basti, dem Betreiber, und kostete ihn 40 bis 50 Stunden im Monat.',
      steps: [
        { title: 'Bookings von Hand in den Kalender', desc: 'Jeder Act, jedes Datum, jede Uhrzeit wurde manuell eingetragen.' },
        { title: 'Jedes Event auf jeder Plattform neu', desc: 'Dieselben Infos mussten auf mehreren Eventplattformen einzeln angelegt werden.' },
        { title: 'Monatsflyer gestalten', desc: 'Für jeden Monat entstand ein neuer Flyer mit dem kompletten Programm.' },
        { title: 'Social-Media-Posts vorbereiten', desc: 'Posts für den Monat und für jeden einzelnen Abend mussten erstellt und geplant werden.' },
        { title: 'Täglich das Event des Tages teilen', desc: 'Jeden Tag wieder: das heutige Event raussuchen und von Hand teilen.' },
        { title: 'Reservierungen nachhalten', desc: 'Reservierungen mussten händisch erfasst und abgeglichen werden.' },
      ],
      punchline: 'Dieselben Daten, immer wieder von Hand eingegeben. Jeden Monat.',
      total: 'Summe pro Monat',
      totalValue: '40–50 Std',
    },
    solution: {
      label: 'CASE STUDY / LÖSUNG',
      title: 'Ein System,',
      highlight: 'EINE EINGABE',
      description: 'CRE8NOW hat den Prozess nicht beschleunigt, sondern ersetzt. Statt Basti ein weiteres Tool hinzustellen, sind drei Bausteine entstanden, die über eine gemeinsame Datenbank miteinander sprechen: eine Booking-App für den Mac, eine Chrome-Extension als Companion und die neue Website.',
      flow: [
        { title: 'Booking Mac-App', desc: 'Die Zentrale für alles: Basti trägt jedes Booking ein einziges Mal ein, die App hält Programm, Acts und Reservierungen.' },
        { title: 'Chrome-Companion', desc: 'Synct jedes Event auf alle Plattformen und teilt täglich das Event des Tages.' },
        { title: 'Website', desc: 'Zeigt das Programm live und schickt Reservierungen zurück in die App.' },
      ],
      noAi: {
        title: 'Komplett ohne laufende Kosten.',
        copy: 'Das ganze System läuft über eigene, von CRE8NOW entwickelte Lösungen im Browser und auf dem Mac. Keine KI-Dienste, keine Abos, keine API-Kosten. Der laufende Betrieb kostet Basti keinen Cent.',
        value: '0 €',
        valueLabel: 'laufende Kosten',
      },
      screensLabel: 'Einblicke ins System',
      parts: [
        {
          kicker: 'Baustein 01',
          title: 'Booking-App für macOS',
          desc: 'Das Zentrum des Systems. Hier legt Basti Bookings an, sieht den Monat auf einen Blick und verwaltet eingehende Reservierungen. Alle Daten liegen in einer Datenbank, aus der auch Companion und Website lesen.',
          tags: ['macOS', 'Kalender', 'Reservierungen', 'Datenbank-Sync'],
          media: 'Bild/Video: Booking-App',
        },
        {
          kicker: 'Baustein 02',
          title: 'Chrome-Companion',
          desc: 'Die Extension übernimmt die Arbeit im Browser: Sie spielt jedes Event auf die Eventplattformen aus, teilt jeden Tag das aktuelle Event und übernimmt das Social Sharing. Was früher Stunden an Copy & Paste war, läuft jetzt im Hintergrund.',
          tags: ['Chrome Extension', 'Event-Sync', 'Social Sharing', 'Daily Post'],
          media: 'Bild/Video: Chrome-Companion',
        },
        {
          kicker: 'Baustein 03',
          title: 'Website minimal.berlin',
          desc: 'Nach 16 Jahren die erste eigene Website der Bar. Programm, Artists, Räume und Reservierung an einem Ort, gespeist aus derselben Datenbank wie die App. Reservierungsanfragen landen direkt im Tool.',
          tags: ['Website', 'Live-Programm', 'Reservierung', 'Automatisch aktuell'],
          media: 'Bild/Video: Website',
          link: { href: 'https://minimal.berlin', label: 'minimal.berlin ansehen' },
        },
      ],
    },
    website: {
      label: 'CASE STUDY / WEBSITE',
      title: 'Gebaut für',
      highlight: 'SUCHE & KI',
      description: 'Die neue Website pflegt sich selbst: Programm und Events kommen automatisch aus der Booking-App, niemand muss sie von Hand aktualisieren. Gebaut ist sie für Ladezeit und Conversion, für Barrierefreiheit, für Suchmaschinen und für die Sprachmodelle, über die Gäste heute eine Bar finden.',
      scores: { performance: 'Leistung', accessibility: 'Barrierefreiheit', bestPractices: 'Best Practices', seo: 'SEO', agentic: 'Agentisches Browsing' },
      scoresCaption: 'Google Lighthouse · minimal.berlin · Mobil',
      pillars: [
        { icon: RefreshCw, title: 'Automatisch aktuell', desc: 'Programm und Events werden direkt aus der Booking-App gespeist. Die Website ist immer auf dem Stand des Kalenders, ohne einen einzigen Handgriff.' },
        { icon: Zap, title: 'Performance', desc: 'Vorgerendert und statisch ausgeliefert: Inhalte stehen sofort als fertiges HTML bereit, ohne dass bei jedem Aufruf erst ein Server rechnen muss.' },
        { icon: MousePointerClick, title: 'Conversion', desc: 'Klare Wege zu dem, was Gäste wirklich wollen: Was läuft heute, wie komme ich hin, wie reserviere ich. Jeder Abschnitt führt auf eine dieser Fragen hin.' },
        { icon: Accessibility, title: 'Barrierefreiheit', desc: 'Semantisches HTML, geprüfte Kontraste, beschriftete Bedienelemente und Alt-Texte, damit die Seite für alle funktioniert.' },
        { icon: Search, title: 'SEO', desc: 'Saubere Metadaten, Sitemap und strukturierte Daten zu Bar, Adresse und Öffnungszeiten, damit Google die Minimal Bar richtig versteht.' },
        { icon: Bot, title: 'LLM-ready', desc: 'Eine llms.txt und strukturierte Daten machen Öffnungszeiten, Adresse und Angebot für ChatGPT, Perplexity, Gemini & Co. direkt lesbar.' },
      ],
    },
    compare: {
      label: 'CASE STUDY / VORHER · NACHHER',
      title: 'Jeder Handgriff',
      highlight: 'AUTOMATISIERT',
      items: [
        { problem: 'Bookings von Hand eintragen', response: 'Ein Eintrag in der Booking-App, alles andere läuft automatisch daraus.', outcome: '1× Eingabe, Rest automatisch' },
        { problem: 'Events auf jeder Plattform', response: 'Der Chrome-Companion synct jedes Event automatisch auf alle Plattformen.', outcome: 'Automatisch gesynct' },
        { problem: 'Monatsflyer zusammentragen', response: 'Aus dem Monatsprogramm in der App entsteht der Flyer automatisch.', outcome: 'Flyer automatisch erstellt' },
        { problem: 'Social Posts vorbereiten', response: 'Der Companion teilt jedes Event automatisch auf Social Media.', outcome: 'Social automatisch geteilt' },
        { problem: 'Täglich das Event teilen', response: 'Jeden Tag teilt der Companion das Event des Tages automatisch.', outcome: 'Täglich automatisch geteilt' },
        { problem: 'Reservierungen nachhalten', response: 'Anfragen über die Website landen automatisch in der Booking-App.', outcome: 'Reservierungen automatisch im Tool' },
      ],
    },
    result: {
      before: 'Vorher',
      after: 'Heute',
      beforeValue: '~50 Std / Monat',
      afterValue: '30 Min / Monat',
      saving: 'rund 99 % weniger Handarbeit',
      copy: [
        'Aus 40 bis 50 Stunden repetitiver Arbeit pro Monat wurden rund 30 Minuten. Basti pflegt sein Programm an einer einzigen Stelle, der Rest passiert von selbst. Das spart nicht nur Zeit, es nimmt ihm eine Menge Stress von den Schultern.',
        'Mit der neuen Website schließt sich der Kreis: Zum ersten Mal in 16 Jahren laufen Programm, Eventplattformen, Social Media und Reservierungen über ein einziges System. Die digitale Journey der Minimal Bar ist damit komplett.',
      ],
    },
    approach: {
      label: 'CASE STUDY / ANSATZ',
      title: 'Um die Ecke',
      highlight: 'GEDACHT',
      description: 'Die naheliegende Lösung wäre ein weiteres SaaS-Tool gewesen. CRE8NOW hat stattdessen geschaut, wo die Arbeit wirklich passiert, und das System genau dort angesetzt.',
      items: [
        { icon: Chrome, title: 'Dort arbeiten, wo die Arbeit passiert', desc: 'Die Chrome-Extension arbeitet direkt im Browser, genau dort, wo die Eventplattformen ohnehin bedient werden.' },
        { icon: Database, title: 'Eine Quelle für alles', desc: 'App, Companion und Website lesen aus derselben Datenbank. Eine Änderung in der App ist sofort überall.' },
        { icon: Users, title: 'Um einen Menschen herum gebaut', desc: 'Das System folgt Bastis Alltag: Er pflegt das Programm einmal, alles andere läuft im Hintergrund.' },
        { icon: Laptop, title: 'Native, wo es zählt', desc: 'Die Booking-App läuft nativ auf dem Mac, schnell und vertraut, statt in einem weiteren Browser-Tab.' },
        { icon: Globe, title: 'Website als Teil des Systems', desc: 'Die Website ist kein Schaufenster nebenher, sondern liest und schreibt in dieselben Daten wie die App.' },
        { icon: Target, title: 'Gemessen am Ergebnis', desc: 'Der Maßstab war nicht die Zahl der Features, sondern die Stunden, die Basti jeden Monat zurückbekommt.' },
      ],
    },
    cta: {
      micro: 'NEXT STEP',
      title: 'Wo verliert euer Team',
      highlight: 'STUNDEN?',
      description: 'Fast jedes Unternehmen hat Abläufe, in denen dieselben Daten mehrfach von Hand eingegeben werden. CRE8NOW findet diese Schleifen und automatisiert sie.',
      cta: 'Erstgespräch starten',
    },
  },
  en: {
    htmlLang: 'en',
    path: '/en/case-studies/minimal-bar',
    title: 'CRE8NOW | Case Study Minimal Bar Berlin: 50 hrs → 30 min',
    description: 'Booking Mac app, Chrome companion and website in one system: Minimal Bar Berlin went from ~50 hours of manual work a month to 30 minutes. €0 running costs.',
    ogImage: '/assets/og/cre8now_case-minimal-bar-en.jpg',
    breadcrumb: ['CRE8NOW', 'CASE STUDIES', 'MINIMAL BAR'],
    hero: {
      headline: ['From 40–50 hours of manual work to', '30 MINUTES'],
      copy: 'Every month, Minimal Bar in Berlin-Friedrichshain put 40 to 50 hours into the same tasks: bookings into the calendar, every event onto every platform one by one, flyers, social posts, reservations. CRE8NOW turned that into a system: a Mac app, a Chrome companion and the bar\'s first website in 16 years. Today, a single entry is enough.',
      cta: 'Visit minimal.berlin',
      marquee: ['Booking app for macOS', 'Chrome companion', 'Event sync', 'Social sharing', 'Reservations', 'Website minimal.berlin', 'No AI', '€0 running costs', 'LLM-ready', '40–50 h → 30 min'],
    },
    stats: [
      { n: '~50 hrs', l: 'manual work per month before' },
      { n: '30 min', l: 'per month today' },
      { n: '€0', l: 'running costs' },
    ],
    problem: {
      label: 'CASE STUDY / STARTING POINT',
      title: 'Every month',
      highlight: 'FROM SCRATCH',
      description: 'Before CRE8NOW came in, Minimal Bar\'s entire programme ran on manual work. Every booking, every event and every post was created by hand, month after month, again on every platform. It all depended on Basti, the owner, and cost him 40 to 50 hours a month.',
      steps: [
        { title: 'Bookings into the calendar by hand', desc: 'Every act, every date, every time slot was entered manually.' },
        { title: 'Every event on every platform', desc: 'The same details had to be created separately on several event platforms.' },
        { title: 'Designing the monthly flyer', desc: 'Each month needed a new flyer with the full programme.' },
        { title: 'Preparing social media posts', desc: 'Posts for the month and for every single night had to be created and scheduled.' },
        { title: 'Sharing tonight\'s event, daily', desc: 'Every single day: find today\'s event and share it by hand.' },
        { title: 'Keeping track of reservations', desc: 'Reservations had to be logged and reconciled manually.' },
      ],
      punchline: 'The same data, typed in by hand again and again. Every month.',
      total: 'Total per month',
      totalValue: '40–50 hrs',
    },
    solution: {
      label: 'CASE STUDY / SOLUTION',
      title: 'One system,',
      highlight: 'ONE ENTRY',
      description: 'CRE8NOW didn\'t speed the process up, it replaced it. Instead of handing Basti yet another tool, we built three components that talk to each other through a shared database: a booking app for the Mac, a Chrome extension as a companion, and the new website.',
      flow: [
        { title: 'Booking Mac app', desc: 'The hub for everything: Basti enters each booking exactly once, the app holds programme, acts and reservations.' },
        { title: 'Chrome companion', desc: 'Syncs every event to all platforms and shares tonight\'s event every day.' },
        { title: 'Website', desc: 'Shows the programme live and sends reservations back into the app.' },
      ],
      noAi: {
        title: 'No running costs at all.',
        copy: 'The whole system runs on CRE8NOW\'s own solutions in the browser and on the Mac. No AI services, no subscriptions, no API fees. Running it costs Basti nothing.',
        value: '€0',
        valueLabel: 'running costs',
      },
      screensLabel: 'Inside the system',
      parts: [
        {
          kicker: 'Component 01',
          title: 'Booking app for macOS',
          desc: 'The heart of the system. This is where Basti creates bookings, sees the month at a glance and manages incoming reservations. All data lives in one database that the companion and the website read from too.',
          tags: ['macOS', 'Calendar', 'Reservations', 'Database sync'],
          media: 'Image/video: booking app',
        },
        {
          kicker: 'Component 02',
          title: 'Chrome companion',
          desc: 'The extension does the work in the browser: it publishes every event to the event platforms, shares the current event every day and handles social sharing. What used to be hours of copy and paste now runs in the background.',
          tags: ['Chrome extension', 'Event sync', 'Social sharing', 'Daily post'],
          media: 'Image/video: Chrome companion',
        },
        {
          kicker: 'Component 03',
          title: 'Website minimal.berlin',
          desc: 'The bar\'s first own website in 16 years. Programme, artists, rooms and reservations in one place, fed from the same database as the app. Reservation requests land directly in the tool.',
          tags: ['Website', 'Live programme', 'Reservations', 'Always up to date'],
          media: 'Image/video: website',
          link: { href: 'https://minimal.berlin', label: 'Visit minimal.berlin' },
        },
      ],
    },
    website: {
      label: 'CASE STUDY / WEBSITE',
      title: 'Built for',
      highlight: 'SEARCH & AI',
      description: 'The new website maintains itself: programme and events come straight from the booking app, nobody has to update it by hand. It is built for load time and conversion, for accessibility, for search engines, and for the language models guests now use to find a bar.',
      scores: { performance: 'Performance', accessibility: 'Accessibility', bestPractices: 'Best Practices', seo: 'SEO', agentic: 'Agentic Browsing' },
      scoresCaption: 'Google Lighthouse · minimal.berlin · mobile',
      pillars: [
        { icon: RefreshCw, title: 'Always up to date', desc: 'Programme and events are fed directly from the booking app. The website always matches the calendar, without a single manual step.' },
        { icon: Zap, title: 'Performance', desc: 'Prerendered and served statically: content arrives as finished HTML, with no server computing every single request.' },
        { icon: MousePointerClick, title: 'Conversion', desc: 'Clear paths to what guests actually want: what\'s on tonight, how do I get there, how do I reserve. Every section leads to one of those answers.' },
        { icon: Accessibility, title: 'Accessibility', desc: 'Semantic HTML, checked contrast, labelled controls and alt text, so the site works for everyone.' },
        { icon: Search, title: 'SEO', desc: 'Clean metadata, a sitemap and structured data for the bar, its address and opening hours, so Google understands Minimal Bar correctly.' },
        { icon: Bot, title: 'LLM-ready', desc: 'An llms.txt and structured data make opening hours, address and what the bar offers directly readable for ChatGPT, Perplexity, Gemini and co.' },
      ],
    },
    compare: {
      label: 'CASE STUDY / BEFORE · AFTER',
      title: 'Every manual step',
      highlight: 'AUTOMATED',
      items: [
        { problem: 'Entering bookings by hand', response: 'One entry in the booking app, everything else follows automatically.', outcome: '1× entry, rest automatic' },
        { problem: 'Events on every platform', response: 'The Chrome companion syncs every event to all platforms automatically.', outcome: 'Synced automatically' },
        { problem: 'Compiling the monthly flyer', response: 'The flyer is generated automatically from the monthly programme in the app.', outcome: 'Flyer created automatically' },
        { problem: 'Preparing social posts', response: 'The companion shares every event to social media automatically.', outcome: 'Social shared automatically' },
        { problem: 'Sharing the event daily', response: 'Every day, the companion shares tonight\'s event automatically.', outcome: 'Shared daily, automatically' },
        { problem: 'Tracking reservations', response: 'Requests made on the website land in the booking app automatically.', outcome: 'Reservations in the tool, automatically' },
      ],
    },
    result: {
      before: 'Before',
      after: 'Today',
      beforeValue: '~50 hrs / month',
      afterValue: '30 min / month',
      saving: 'around 99 % less manual work',
      copy: [
        '40 to 50 hours of repetitive work a month became around 30 minutes. Basti maintains his programme in one single place, and the rest happens on its own. That saves more than time: it takes a lot of stress off his shoulders.',
        'The new website closes the loop: for the first time in 16 years, programme, event platforms, social media and reservations all run through one system. Minimal Bar\'s digital journey is complete.',
      ],
    },
    approach: {
      label: 'CASE STUDY / APPROACH',
      title: 'Thinking',
      highlight: 'AROUND CORNERS',
      description: 'The obvious answer would have been yet another SaaS tool. Instead, CRE8NOW looked at where the work actually happens and built the system right there.',
      items: [
        { icon: Chrome, title: 'Work where the work happens', desc: 'The Chrome extension operates directly in the browser, exactly where the event platforms are used anyway.' },
        { icon: Database, title: 'One source for everything', desc: 'App, companion and website read from the same database. A change in the app is everywhere instantly.' },
        { icon: Users, title: 'Built around one person', desc: 'The system follows Basti\'s day: he maintains the programme once, everything else runs in the background.' },
        { icon: Laptop, title: 'Native where it counts', desc: 'The booking app runs natively on the Mac, fast and familiar, instead of in yet another browser tab.' },
        { icon: Globe, title: 'The website is part of the system', desc: 'The website isn\'t a shop window on the side: it reads and writes the same data as the app.' },
        { icon: Target, title: 'Measured by the outcome', desc: 'The yardstick wasn\'t the number of features but the hours Basti gets back every month.' },
      ],
    },
    cta: {
      micro: 'NEXT STEP',
      title: 'Where is your team',
      highlight: 'LOSING HOURS?',
      description: 'Almost every company has workflows where the same data is typed in by hand several times. CRE8NOW finds those loops and automates them.',
      cta: 'Start discovery call',
    },
  },
};

/* Media slot — renders the final asset once MEDIA has a path, otherwise a
   hatched placeholder in the exact ratio the asset will fill. */
/* Muted loop that only loads and plays while on screen — a 5 MB recording
   shouldn't download for visitors who never scroll to it. */
const LazyVideo = ({ src, poster, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.25 });
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (inView) {
      // Source is attached on first view only, so nothing downloads before.
      // src may be a list of {src, type}: first one the browser can play
      // wins (HEVC where supported, H.264 otherwise).
      if (!v.getAttribute('src')) {
        const pick = Array.isArray(src) ? (src.find((o) => v.canPlayType(o.type)) || src[src.length - 1]).src : src;
        v.setAttribute('src', pick);
      }
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [inView, src]);
  return <video ref={ref} poster={poster || undefined} muted loop playsInline preload="none" className={className} />;
};

const MediaSlot = ({ src, poster, type = 'image', ratio = '16/9', label, className = '' }) => {
  const isVideo = type === 'video' || Array.isArray(src) || /\.(mp4|webm|mov)$/i.test(src || '');
  if (src) {
    // Images keep their own aspect ratio (screenshots must not be cropped);
    // the ratio only shapes videos and placeholders.
    return (
      <div className={`relative overflow-hidden ${className}`} style={isVideo ? { aspectRatio: ratio } : undefined}>
        {isVideo ? (
          <LazyVideo src={src} poster={poster} className="w-full h-full object-cover" />
        ) : (
          <img src={src} alt={label} loading="lazy" decoding="async" className="block w-full h-auto" />
        )}
      </div>
    );
  }
  const Icon = isVideo ? Play : ImageIcon;
  return (
    <div
      className={`relative overflow-hidden flex items-center justify-center text-text-color ${className}`}
      style={{
        aspectRatio: ratio,
        backgroundColor: 'var(--surface-color)',
        backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 14px, color-mix(in srgb, currentColor 6%, transparent) 14px 15px)',
      }}
      role="img"
      aria-label={label}
    >
      <CropMarks inset={10} color={ACCENT} className="opacity-60" />
      <div className="flex flex-col items-center gap-3 text-center px-6">
        <span className="w-12 h-12 md:w-14 md:h-14 rounded-full border border-border-color flex items-center justify-center" style={{ color: ACCENT }}>
          <Icon size={20} />
        </span>
        <span className="font-mono-swiss text-[9px] md:text-[10px] uppercase tracking-[0.25em] opacity-60">{label}</span>
        <span className="font-mono-swiss text-[8px] uppercase tracking-[0.25em] opacity-35">Placeholder · {ratio.replace('/', ':')}</span>
      </div>
    </div>
  );
};

/* Lighthouse's own rating colors and thresholds (0–49 fail, 50–89
   average, 90–100 pass), light and dark, so the rings read as the real
   report at a glance. */
const LH_COLORS = {
  pass: { light: '#008800', dark: '#0cce6b', ring: '#0cce6b', fill: 'rgba(12,206,107,0.08)', track: 'rgba(12,206,107,0.18)' },
  average: { light: '#c33300', dark: '#ffa400', ring: '#ffa400', fill: 'rgba(255,164,0,0.08)', track: 'rgba(255,164,0,0.18)' },
  fail: { light: '#cc0000', dark: '#ff4e42', ring: '#ff4e42', fill: 'rgba(255,78,66,0.08)', track: 'rgba(255,78,66,0.18)' },
};
const lhRating = (score) => (score >= 90 ? 'pass' : score >= 50 ? 'average' : 'fail');

/* Lighthouse-style score ring — fills and counts up once in view. */
const ScoreGauge = ({ score, label, delay = 0, theme }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? score : 0);
  const R = 44;
  const C = 2 * Math.PI * R;
  const tone = LH_COLORS[lhRating(score)];
  const textColor = theme === 'dark' ? tone.dark : tone.light;

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const controls = animate(0, score, { duration: 1.6, delay, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setValue(Math.round(v)) });
    return () => controls.stop();
  }, [inView, reduce, score, delay]);

  return (
    <div ref={ref} className="flex flex-col items-center gap-4">
      <div className="relative w-[clamp(96px,11vw,148px)] aspect-square">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true">
          <circle cx="50" cy="50" r={R} fill={tone.fill} stroke={tone.track} strokeWidth="6" />
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={tone.ring}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - value / 100)}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-black tabular-nums text-[clamp(26px,3vw,42px)]" style={{ color: textColor }}>
          {value}
        </span>
      </div>
      <span className="font-mono-swiss text-[9px] md:text-[11px] uppercase tracking-[0.2em] text-center opacity-70">{label}</span>
    </div>
  );
};

/* Lighthouse's "Agentic Browsing" result is a pass count, not a score —
   shown as the same pill the report uses, sized to sit in the ring row. */
const AgenticPill = ({ passed, total, label, theme }) => {
  const tone = LH_COLORS[passed === total ? 'pass' : 'average'];
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-[clamp(96px,11vw,148px)] aspect-square flex items-center justify-center">
        <Motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-black tabular-nums text-[clamp(16px,1.6vw,22px)]"
          style={{ backgroundColor: tone.fill.replace('0.08', '0.14'), color: theme === 'dark' ? tone.dark : tone.light }}
        >
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tone.ring }} />
          {passed}/{total}
        </Motion.span>
      </div>
      <span className="font-mono-swiss text-[9px] md:text-[11px] uppercase tracking-[0.2em] text-center opacity-70">{label}</span>
    </div>
  );
};

const Reveal = ({ children, idx = 0, className = '', style }) => (
  <Motion.div
    style={style}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: Math.min(idx, 6) * 0.06 }}
    viewport={{ once: true, amount: 0.2 }}
    className={className}
  >
    {children}
  </Motion.div>
);

const MinimalBarCaseStudy = ({ lang: siteLang }) => {
  const lang = CONTENT[siteLang] ? siteLang : 'de';
  const c = CONTENT[lang];
  const t = TRANSLATIONS[lang];
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  // Same theme handling as SolutionPageTemplate: honor ?theme= and the
  // stored choice, mirror it onto <html> and back into the URL.
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const fromUrl = new URLSearchParams(window.location.search).get('theme');
      if (fromUrl === 'light' || fromUrl === 'dark') return fromUrl;
      const stored = localStorage.getItem('theme');
      if (stored === 'light' || stored === 'dark') return stored;
    }
    return 'light';
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
    const url = new URL(window.location.href);
    url.searchParams.set('theme', theme);
    window.history.replaceState({}, '', url.toString());
  }, [theme]);

  const posterBg = MAGENTA;
  const posterInk = '#ffffff';
  const posterAccent = YELLOW;
  const posterAccentText = 'var(--cre8-ink)';
  const posterMuted = 'rgba(255,255,255,0.8)';
  const posterBorder = 'rgba(255,255,255,0.25)';

  const openWizard = (source) => {
    trackEvent('case_minimal_bar_cta_click', { source, lang });
    setIsBookingOpen(true);
  };
  const trackOutbound = (source) => trackEvent('case_minimal_bar_outbound', { source, href: SITE_URL, lang });
  const goToTab = (tab) => { window.location.href = `/${lang}/${tab}`; };
  const goToLegal = () => { window.location.href = `/${lang}/impressum`; };
  const switchLang = (l) => { window.location.href = CONTENT[l === 'de' ? 'de' : 'en'].path; };

  // --- SEO head: title, description, canonical, hreflang, JSON-LD ---
  useEffect(() => {
    document.title = c.title;
    document.documentElement.lang = c.htmlLang;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', c.description);
    setMeta('meta[property="og:title"]', 'content', c.title);
    setMeta('meta[property="og:description"]', 'content', c.description);
    setMeta('meta[name="twitter:title"]', 'content', c.title);
    setMeta('meta[name="twitter:description"]', 'content', c.description);
    setMeta('meta[property="og:url"]', 'content', ORIGIN + c.path);
    setMeta('meta[property="og:locale"]', 'content', lang === 'de' ? 'de_DE' : 'en_US');
    const ogImageUrl = ORIGIN + c.ogImage;
    const ogImageAlt = 'Case Study Minimal Bar Berlin — CRE8NOW';
    setMeta('meta[property="og:image"]', 'content', ogImageUrl);
    setMeta('meta[property="og:image:alt"]', 'content', ogImageAlt);
    setMeta('meta[name="twitter:image"]', 'content', ogImageUrl);
    setMeta('meta[name="twitter:image:alt"]', 'content', ogImageAlt);

    const canonicalTag = document.getElementById('canonical-tag');
    if (canonicalTag) canonicalTag.setAttribute('href', ORIGIN + c.path);
    const hreflangDe = document.querySelector('link[rel="alternate"][hreflang="de"]');
    const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
    const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (hreflangDe) hreflangDe.setAttribute('href', ORIGIN + CONTENT.de.path);
    if (hreflangEn) hreflangEn.setAttribute('href', ORIGIN + CONTENT.en.path);
    if (hreflangDefault) hreflangDefault.setAttribute('href', ORIGIN + CONTENT.de.path);

    const ld = [
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'CRE8NOW', item: ORIGIN + '/' },
        { '@type': 'ListItem', position: 2, name: 'Case Studies', item: ORIGIN + c.path },
        { '@type': 'ListItem', position: 3, name: 'Minimal Bar Berlin', item: ORIGIN + c.path },
      ] },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': ORIGIN + c.path + '#case-study',
        headline: c.title.replace('CRE8NOW | ', ''),
        description: c.description,
        inLanguage: c.htmlLang,
        url: ORIGIN + c.path,
        author: { '@id': ORIGIN + '/#organization' },
        publisher: { '@id': ORIGIN + '/#organization' },
        about: { '@type': 'BarOrPub', name: 'Minimal Bar Berlin', url: 'https://minimal.berlin' },
      },
    ];
    const scripts = ld.map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-page', 'case-minimal-bar');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [c, lang]);

  const scoreKeys = ['performance', 'accessibility', 'bestPractices', 'seo'];

  return (
    <Motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="bg-bg-color text-text-color min-h-screen overflow-x-clip"
    >
      <GrainOverlay fixed opacity={0.03} className="z-40" />
      <Nav
        activeTab="ai"
        setActiveTab={goToTab}
        setView={goToLegal}
        color={ACCENT}
        lang={lang}
        setLang={switchLang}
        t={t}
        theme={theme}
        setTheme={setTheme}
        onOpenBookingModal={() => openWizard('nav')}
      />

      {/* ===== HERO ===== */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center gpu-accelerated overflow-hidden" style={{ backgroundColor: posterBg }}>
        <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, ease: 'easeOut' }} className="absolute inset-0 pointer-events-none">
          <GridOverlay columns={12} opacity={0.16} color={posterInk} />
        </Motion.div>
        <div className="hidden md:block absolute inset-0 z-10 pointer-events-none" style={{ color: posterInk }}>
          <CropMarks insetTop={8} insetX={8} showBottom={false} color={posterAccent} />
          <div className="flex absolute top-24 left-6 items-center gap-4">
            <RegistrationMark size={16} style={{ color: posterAccent }} />
            <IndexTag code="CS" num="MB-001" color={posterAccent} />
          </div>
          <div className="flex absolute top-24 right-6 items-center gap-4">
            <LiveCoordTag />
            <RegistrationMark size={16} style={{ color: posterAccent }} />
          </div>
          <div className="absolute bottom-20 md:bottom-24 left-6 flex items-center flex-wrap gap-x-3 gap-y-1 font-mono-swiss text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.25em]">
            {c.breadcrumb.map((part, i) => (
              <React.Fragment key={part}>
                <span style={{ color: i === c.breadcrumb.length - 1 ? posterAccent : undefined }}>{part}</span>
                {i < c.breadcrumb.length - 1 && <span className="opacity-50">/</span>}
              </React.Fragment>
            ))}
          </div>
          <VerticalLabel text="CRE8NOW / CASE STUDY" side="left" offset={24} />
          <VerticalLabel text="MODE / AUTOMATION" side="right" offset={24} />
        </div>

        <div className="hero-stack relative z-20 text-center px-6" style={{ color: posterInk }}>
          <Motion.h4
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]"
          >
            {c.hero.headline[0]}
          </Motion.h4>
          <Motion.h1
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4"
          >
            <span className="inline-block whitespace-nowrap audiowide-regular font-normal text-[1.08em] md:text-[1.4em]" style={{ color: posterAccent }}>
              {c.hero.headline[1]}
            </span>
          </Motion.h1>
          <Motion.p
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="max-w-[1100px] w-[90vw] hero-copy mx-auto mt-3 md:-mt-4 leading-relaxed font-light opacity-95"
          >
            {c.hero.copy}
          </Motion.p>
          <Motion.div
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6, ease: [0.83, 0, 0.17, 1] }}
            className="flex items-center justify-center mt-10"
          >
            <PrimaryLink href={SITE_URL} onClick={() => trackOutbound('hero')} bgColor={posterInk} textColor={posterBg} hoverFill={posterAccent} hoverTextColor={posterAccentText}>
              {c.hero.cta}
            </PrimaryLink>
          </Motion.div>
        </div>

        <Motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
          className="absolute bottom-0 left-0 z-20 w-full overflow-hidden border-t"
          style={{ borderColor: posterBorder }}
        >
          <HeroMarqueeTrack items={c.hero.marquee} ink={posterInk} accent={posterAccent} bg={posterBg} />
        </Motion.div>
      </section>

      {/* ===== STATS ===== */}
      <StatsStrip items={c.stats.map((s) => ({ value: s.n, label: s.l }))} color={ACCENT} />

      {/* ===== CASE FILM — the sitewide teaser player and frame (same as the
          Prototyping and Concierge pages): loops muted, the play mark
          restarts it from the top with sound. ===== */}
      <section className="py-16 md:py-24 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20 flex flex-col items-center">
          <TeaserVideo src={MEDIA.walkthrough} poster={MEDIA.walkthroughPoster} />
        </div>
      </section>

      {/* ===== 01 — PROBLEM ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="CS" num="MB-002" color={ACCENT} verticalLabelRight="MODE / PROBLEM" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={1} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.problem.label} title={c.problem.title} highlight={c.problem.highlight} description={c.problem.description} color={ACCENT} theme={theme} centered />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
            {c.problem.steps.map((step, i) => (
              <Reveal key={step.title} idx={i} className="border-t border-border-color pt-6">
                <span className="font-mono-swiss text-[10px] tabular-nums opacity-40">{String(i + 1).padStart(2, '0')}</span>
                <h5 className="font-black tracking-tight text-xl md:text-2xl mt-3 mb-3">{step.title}</h5>
                <p className="text-text-muted font-light leading-relaxed">{step.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 md:mt-20 grid md:grid-cols-[1fr_auto] items-center gap-8 p-8 md:p-12 bg-surface-color">
            <h4 className="!mt-0">{c.problem.punchline}</h4>
            <div className="text-left md:text-right">
              <p className="font-mono-swiss text-[9px] md:text-[10px] uppercase tracking-[0.25em] opacity-50 mb-2">{c.problem.total}</p>
              <h3 className="leading-none whitespace-nowrap !mb-0" style={{ color: ACCENT }}>{c.problem.totalValue}</h3>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 02 — SOLUTION: flow + three components ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="CS" num="MB-003" color={ACCENT} verticalLabelRight="MODE / SYSTEM" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={2} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.solution.label} title={c.solution.title} highlight={c.solution.highlight} description={c.solution.description} color={ACCENT} theme={theme} centered />

          {/* The result first: from ~50 hours to 30 minutes, then how. */}
          {/* Before/after to scale: 30 min against ~45 h is about 1 %. */}
          <div className="max-w-[1100px] mx-auto space-y-8">
            {[
              { label: c.result.before, value: c.result.beforeValue, width: 100, color: 'var(--text-muted)', opacity: 0.25 },
              { label: c.result.after, value: c.result.afterValue, width: 1.1, color: ACCENT, opacity: 1 },
            ].map((bar) => (
              <div key={bar.label}>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-mono-swiss text-[10px] md:text-[11px] uppercase tracking-[0.25em] opacity-60">{bar.label}</span>
                  <span className="font-black text-lg md:text-2xl tabular-nums" style={{ color: bar.opacity === 1 ? ACCENT : undefined }}>{bar.value}</span>
                </div>
                <div className="h-4 md:h-6 w-full bg-surface-color overflow-hidden">
                  <Motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `max(${bar.width}%, 8px)` }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full"
                    style={{ backgroundColor: bar.color, opacity: bar.opacity }}
                  />
                </div>
              </div>
            ))}
            <p className="text-center font-black uppercase tracking-widest text-sm md:text-base pt-4" style={{ color: ACCENT }}>{c.result.saving}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 md:gap-16 max-w-[1100px] mx-auto mt-16 md:mt-24">
            {c.result.copy.map((para, i) => (
              <Reveal key={i} idx={i}>
                <p className="text-text-muted font-light leading-relaxed text-lg">{para}</p>
              </Reveal>
            ))}
          </div>

          <div className="flex justify-center mt-12 md:mt-16 mb-24 md:mb-32">
            <PrimaryLink href={SITE_URL} onClick={() => trackOutbound('solution')} bgColor="var(--text-color)" textColor="var(--bg-color)" hoverFill={ACCENT} hoverTextColor="#ffffff">
              {c.hero.cta}
            </PrimaryLink>
          </div>


          {/* Flow: booking app → companion → website, arrows turn downward
              when the row stacks on mobile. */}
          <div className="grid lg:grid-cols-[1fr_32px_1fr_32px_1fr] gap-4 lg:gap-3 items-stretch">
            {c.solution.flow.map((step, i) => (
              <React.Fragment key={step.title}>
                <Reveal idx={i} className="relative p-6 md:p-8 border border-border-color h-full" >
                  <span className="font-mono-swiss text-[10px] tabular-nums" style={{ color: ACCENT }}>{String(i + 1).padStart(2, '0')}</span>
                  <h5 className="font-black tracking-tight text-xl md:text-2xl mt-3 mb-2">{step.title}</h5>
                  <p className="text-text-muted font-light leading-relaxed text-sm md:text-base">{step.desc}</p>
                </Reveal>
                {i < c.solution.flow.length - 1 && (
                  <ArrowRight size={20} className="justify-self-center self-center opacity-60 rotate-90 lg:rotate-0" style={{ color: ACCENT }} />
                )}
              </React.Fragment>
            ))}
          </div>

          <Reveal className="mt-16 md:mt-20 grid md:grid-cols-[1fr_auto] items-center gap-8 p-8 md:p-12 border-2" style={{ borderColor: ACCENT }}>
            <div className="contents">
              <div>
                <h4 className="!mt-0" style={{ color: ACCENT }}>{c.solution.noAi.title}</h4>
                <p className="text-text-muted font-light leading-relaxed mt-4 max-w-3xl">{c.solution.noAi.copy}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="stat-value" style={{ color: ACCENT }}>{c.solution.noAi.value}</p>
                <p className="stat-label mt-2">{c.solution.noAi.valueLabel}</p>
              </div>
            </div>
          </Reveal>

          <div className="mt-24 md:mt-32 space-y-24 md:space-y-32">
            {c.solution.parts.map((part, i) => (
              <div key={part.title} className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
                <Reveal className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <MediaSlot {...[
                    { src: MEDIA.app, poster: MEDIA.appPoster, ratio: '16/9' },
                    { src: MEDIA.companion, poster: MEDIA.companionPoster, ratio: '4/3' },
                    { src: MEDIA.website, poster: MEDIA.websitePoster, ratio: '16/10' },
                  ][i]} label={part.media} />
                </Reveal>
                <Reveal idx={1}>
                  <span className="font-mono-swiss text-[10px] uppercase tracking-[0.25em]" style={{ color: ACCENT }}>{part.kicker}</span>
                  <h3 className="mt-4 !text-[clamp(32px,3.4vw,60px)]">{part.title}</h3>
                  <p className="text-text-muted font-light leading-relaxed mt-6">{part.desc}</p>
                  <div className="flex flex-wrap gap-2 mt-8">
                    {part.tags.map((tag) => (
                      <span key={tag} className="text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border border-border-color text-text-muted">{tag}</span>
                    ))}
                  </div>
                  {part.link && (
                    <div className="mt-8">
                      <PrimaryLink href={SITE_URL} onClick={() => trackOutbound('component')} bgColor="var(--text-color)" textColor="var(--bg-color)" hoverFill={ACCENT} hoverTextColor="#ffffff">
                        {part.link.label}
                      </PrimaryLink>
                    </div>
                  )}
                </Reveal>
              </div>
            ))}
          </div>

          <div className="mt-24 md:mt-32 space-y-16 md:space-y-20">
            <p className="font-mono-swiss text-[10px] uppercase tracking-[0.25em] text-center" style={{ color: ACCENT }}>{c.solution.screensLabel}</p>
            {MEDIA.screenGroups.map((group) => (
              <div key={group.en}>
                <h5 className="mb-6">{group[lang]}</h5>
                <div className={`grid md:grid-cols-2 ${group.items.length === 4 ? '' : 'lg:grid-cols-3'} gap-8 md:gap-6`}>
                  {group.items.map((screen, i) => (
                    <Reveal key={screen.en} idx={i}>
                      <MediaSlot src={screen.src} ratio="16/10" label={screen[lang]} />
                      <p className="text-text-muted font-light text-sm mt-4">{screen[lang]}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 03 — WEBSITE: Lighthouse scores + what's built in ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="CS" num="MB-004" color={ACCENT} verticalLabelRight="MODE / WEBSITE" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={3} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.website.label} title={c.website.title} highlight={c.website.highlight} description={c.website.description} color={ACCENT} theme={theme} centered />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-12 gap-x-6 max-w-[1300px] mx-auto">
            {scoreKeys.map((key, i) => (
              <ScoreGauge key={key} score={LIGHTHOUSE[key]} label={c.website.scores[key]} delay={i * 0.15} theme={theme} />
            ))}
            <AgenticPill passed={AGENTIC_BROWSING.passed} total={AGENTIC_BROWSING.total} label={c.website.scores.agentic} theme={theme} />
          </div>
          <p className="font-mono-swiss text-[9px] md:text-[10px] uppercase tracking-[0.25em] opacity-40 text-center mt-10">{c.website.scoresCaption}</p>
          <div className="flex justify-center mt-10">
            <PrimaryLink href={SITE_URL} onClick={() => trackOutbound('scores')} bgColor="var(--text-color)" textColor="var(--bg-color)" hoverFill={ACCENT} hoverTextColor="#ffffff">
              {c.hero.cta}
            </PrimaryLink>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 mt-20 md:mt-28">
            {c.website.pillars.map(({ icon, title, desc }, i) => {
              const Icon = icon;
              return (
              <Reveal key={title} idx={i} className="border-t border-border-color pt-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-swiss text-[10px] tabular-nums opacity-40">{String(i + 1).padStart(2, '0')}</span>
                  <Icon size={26} strokeWidth={1.5} style={{ color: ACCENT }} className="opacity-80" />
                </div>
                <h5 className="font-black tracking-tight text-xl md:text-2xl mb-3">{title}</h5>
                <p className="text-text-muted font-light leading-relaxed">{desc}</p>
              </Reveal>
            );
            })}
          </div>
        </div>
      </section>

      {/* ===== 04 — BEFORE / AFTER ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="CS" num="MB-005" color={ACCENT} verticalLabelRight="MODE / COMPARE" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={4} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.compare.label} title={c.compare.title} highlight={c.compare.highlight} color={ACCENT} theme={theme} centered />
          <div className="space-y-12 md:space-y-8">
            {c.compare.items.map((item, i) => (
              <SolveRow key={item.problem} item={item} idx={i} color={ACCENT} fg="#ffffff" />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 05 — APPROACH ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="CS" num="MB-006" color={ACCENT} verticalLabelRight="MODE / APPROACH" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={5} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.approach.label} title={c.approach.title} highlight={c.approach.highlight} description={c.approach.description} color={ACCENT} theme={theme} centered />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {c.approach.items.map(({ icon, title, desc }, i) => {
              const Icon = icon;
              return (
              <Reveal key={title} idx={i} className="border-t border-border-color pt-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-swiss text-[10px] tabular-nums opacity-40">{String(i + 1).padStart(2, '0')}</span>
                  <Icon size={26} strokeWidth={1.5} style={{ color: ACCENT }} className="opacity-80" />
                </div>
                <h5 className="font-black tracking-tight text-xl md:text-2xl mb-3">{title}</h5>
                <p className="text-text-muted font-light leading-relaxed">{desc}</p>
              </Reveal>
            );
            })}
          </div>
        </div>
      </section>

      {/* ===== 06 — CTA ===== */}
      <section
        className="relative w-full py-24 md:py-32"
        style={{ backgroundColor: posterBg, color: posterInk, '--color-text-color': posterInk, '--color-text-muted': posterMuted, '--color-border-color': posterBorder }}
      >
        <GridOverlay columns={12} opacity={0.16} color={posterInk} />
        <SectionFrame code="CS" num="MB-007" color={posterAccent} verticalLabelRight="MODE / CTA" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={6} total={TOTAL_SECTIONS} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.cta.micro} title={c.cta.title} highlight={c.cta.highlight} description={c.cta.description} color={posterAccent} theme="dark" centered />
          <div className="flex items-center justify-center -mt-10">
            <PrimaryButton onClick={() => openWizard('final')} bgColor={posterInk} textColor={posterBg} hoverFill={posterAccent} hoverTextColor={posterAccentText}>
              {c.cta.cta}
            </PrimaryButton>
          </div>
        </div>
      </section>

      <SiteFooter theme={theme} activeTab="ai" lang={lang} color={ACCENT} t={t} />

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} lang={lang} color={ACCENT} activeTab="ai" initialReasons={['Automation']} />
    </Motion.div>
  );
};

export default MinimalBarCaseStudy;
